/**
 * Which items a frame's menu shows (Part 04 Q12, frame menu audit
 * 2026-10-27). One model for the wall card (and Pinned tile), the opened
 * item's ⋯ and right-click, the wall's multi-selection and a frame pinned
 * to the desktop's own ⋯ (plan 12); what each item does lives in
 * `frame-menu-actions.ts` and `desktop/pin/desktop-pin-actions.ts`.
 *
 * The rules: the most likely item first, groups by what they do, the
 * destructive end last; an item that cannot run is left out, not greyed.
 * Window controls (Open, Focus, Pin, ←, ×) never repeat a pill button:
 * the card pill has Open and Pin, an opened window's pill has ⛶; a
 * desktop widget has no pill, so its menu has Open.
 */

export type FrameMenuItemId =
  | "size"
  | "unarchive"
  | "open"
  | "focus"
  | "openTask"
  | "copy"
  | "download"
  | "showFile"
  | "pin"
  | "unpin"
  | "pinToDesktop"
  | "unpinFromDesktop"
  | "moveToProject"
  | "archive"
  | "delete";

/**
 * Where the menu opens: a wall card or Pinned tile, an opened item (focus,
 * floating or docked window), several selected cards, or the ⋯ of a frame
 * pinned to the desktop.
 */
export type FrameMenuSurface = "card" | "opened" | "selection" | "pin";

export interface FrameMenuContext {
  surface: FrameMenuSurface;
  /** A Project tool (task board, memory): it belongs to the Project. */
  tool: boolean;
  /**
   * Another project's card in Everything: it archives through its own
   * project; nothing that reads or writes through the current one shows.
   */
  foreign: boolean;
  /** Archived (a selection: every selected item). */
  archived: boolean;
  /**
   * Focus differs from Open (the desktop) or opens several (a selection).
   * An opened item never offers it: its pill's ⛶ does.
   */
  focus: boolean;
  /** The frame belongs to a task. */
  task: boolean;
  /** Copy writes text for it (a card-only frame loads it on click). */
  copy: boolean;
  /** Download has a format for it. */
  download: boolean;
  /**
   * A file linked on this computer (plan 14): Show in Finder or Explorer
   * takes Download's place.
   */
  showFile: boolean;
  /** An opened item's Pin state; `null` where Pin does not apply. */
  pinned: boolean | null;
  /**
   * Whether the frame is pinned to the desktop; `null` where the desktop
   * app cannot pin it (the web, a selection).
   */
  desktopPinned: boolean | null;
  /**
   * A desktop widget's size row has a choice: its type takes more than one
   * widget size (not a graph shown only large, not an editor sized freely).
   */
  widgetSizes: boolean;
}

/** The menu's groups in order; a separator goes between shown groups. */
export interface FrameMenuGroups {
  /** A desktop widget's size row (its type's sizes) leads its menu. */
  size: readonly FrameMenuItemId[];
  /** Unarchive leads an archived item's menu, like the Finder's Put Back. */
  restore: readonly FrameMenuItemId[];
  /** Where it takes you; an opened item's type extras join this group. */
  go: readonly FrameMenuItemId[];
  /** What you take from it. */
  content: readonly FrameMenuItemId[];
  /** Where it lives. */
  place: readonly FrameMenuItemId[];
  /** Its end; an opened item's destructive extras join this group. */
  end: readonly FrameMenuItemId[];
}

export const FRAME_MENU_GROUP_ORDER = [
  "size",
  "restore",
  "go",
  "content",
  "place",
  "end",
] as const;

function shown(
  ...items: readonly (FrameMenuItemId | false)[]
): readonly FrameMenuItemId[] {
  return items.filter((item): item is FrameMenuItemId => item !== false);
}

/**
 * Card: Focus · Open task │ Copy · Download… (or Show in Finder for a
 * file linked here) │ Pin to desktop · Move to
 * project… · Archive │ Delete, an archived card led by Unarchive. Opened:
 * Open task │ Copy · Download… │ Pin · Pin to desktop · Move to project… ·
 * Archive │ Delete. Selection: Focus these windows │ Copy │ Move to
 * project… · Archive (Unarchive) │ Delete. Desktop widget: its sizes (Small
 * · Medium · Large, those its type takes, when it has a choice) │ Open │
 * Copy · Download… │ Unpin from desktop. A tool offers only
 * Focus, Copy, Download and Pin to desktop; another project's card only
 * Pin to desktop and Archive or Unarchive. An archived frame is off the
 * wall, so off the desktop too.
 */
export function frameMenuGroups(context: FrameMenuContext): FrameMenuGroups {
  const { surface, tool, foreign, archived } = context;
  if (surface === "pin") {
    return {
      size: shown(context.widgetSizes && "size"),
      restore: [],
      go: ["open"],
      content: shown(
        context.copy && "copy",
        context.download && "download",
        context.showFile && "showFile",
      ),
      place: ["unpinFromDesktop"],
      end: [],
    };
  }
  const selection = surface === "selection";
  const desktopPin =
    !selection &&
    !archived &&
    context.desktopPinned !== null &&
    (context.desktopPinned ? "unpinFromDesktop" : "pinToDesktop");
  if (foreign) {
    return {
      size: [],
      restore: [],
      go: [],
      content: [],
      place: shown(desktopPin, archived ? "unarchive" : "archive"),
      end: [],
    };
  }
  const owned = !tool;
  return {
    size: [],
    restore: shown(owned && archived && !selection && "unarchive"),
    go: shown(
      surface !== "opened" && context.focus && "focus",
      !selection && context.task && "openTask",
    ),
    content: shown(
      context.copy && "copy",
      !selection && context.download && "download",
      !selection && context.showFile && "showFile",
    ),
    place: shown(
      owned &&
        surface === "opened" &&
        context.pinned !== null &&
        (context.pinned ? "unpin" : "pin"),
      desktopPin,
      owned && "moveToProject",
      owned && !archived && "archive",
      owned && archived && selection && "unarchive",
    ),
    end: shown(owned && "delete"),
  };
}

/** The groups' items in order, for an emptiness check or a test. */
export function frameMenuItems(
  groups: FrameMenuGroups,
): readonly FrameMenuItemId[] {
  return FRAME_MENU_GROUP_ORDER.flatMap((group) => groups[group]);
}
